/**
 * RequirementsTab Component
 * Displays extracted RFP requirements organized by type
 */

import React from 'react';
import { Card, Badge, Button } from '@tracekit/core';
import { useRequirementsExtraction, Requirement } from '../hooks/useRequirementsExtraction';
import { useWorkspace } from '@tracekit/core';
import {
  Loader2,
  AlertCircle,
  RefreshCw,
  CheckCircle,
  FileText,
  AlertTriangle,
  DollarSign,
  Shield,
  GraduationCap,
  Upload,
  Scale,
  BarChart2,
  FileCheck,
} from 'lucide-react';

interface RequirementsTabProps {
  opportunityId: string | null;
}

const REQUIREMENT_TYPE_CONFIG: Record<string, { label: string; icon: React.ReactNode; color: string; bgColor: string }> = {
  mandatory: {
    label: 'Mandatory',
    icon: <CheckCircle className="h-4 w-4" />,
    color: 'text-red-700 dark:text-red-300',
    bgColor: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800',
  },
  technical: {
    label: 'Technical',
    icon: <FileText className="h-4 w-4" />,
    color: 'text-blue-700 dark:text-blue-300',
    bgColor: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800',
  },
  commercial: {
    label: 'Commercial',
    icon: <DollarSign className="h-4 w-4" />,
    color: 'text-green-700 dark:text-green-300',
    bgColor: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800',
  },
  administrative: {
    label: 'Administrative',
    icon: <FileCheck className="h-4 w-4" />,
    color: 'text-purple-700 dark:text-purple-300',
    bgColor: 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800',
  },
  legal: {
    label: 'Legal',
    icon: <Scale className="h-4 w-4" />,
    color: 'text-orange-700 dark:text-orange-300',
    bgColor: 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800',
  },
  qualification: {
    label: 'Qualification',
    icon: <GraduationCap className="h-4 w-4" />,
    color: 'text-indigo-700 dark:text-indigo-300',
    bgColor: 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-200 dark:border-indigo-800',
  },
  submission: {
    label: 'Submission',
    icon: <Upload className="h-4 w-4" />,
    color: 'text-cyan-700 dark:text-cyan-300',
    bgColor: 'bg-cyan-50 dark:bg-cyan-900/20 border-cyan-200 dark:border-cyan-800',
  },
  evaluation: {
    label: 'Evaluation',
    icon: <BarChart2 className="h-4 w-4" />,
    color: 'text-pink-700 dark:text-pink-300',
    bgColor: 'bg-pink-50 dark:bg-pink-900/20 border-pink-200 dark:border-pink-800',
  },
  general: {
    label: 'General',
    icon: <FileText className="h-4 w-4" />,
    color: 'text-gray-700 dark:text-gray-300',
    bgColor: 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700',
  },
};

export function RequirementsTab({ opportunityId }: RequirementsTabProps) {
  const { projectId } = useWorkspace();
  
  const {
    data: requirementsData,
    isLoading,
    isError,
    error,
    refetch,
    isRefetching,
  } = useRequirementsExtraction(opportunityId, projectId || undefined, !!opportunityId && !!projectId);

  // Loading State
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 text-blue-600 dark:text-blue-400 animate-spin" />
          <p className="text-sm text-gray-600 dark:text-slate-200">
            Extracting requirements from opportunity documents...
          </p>
        </div>
      </div>
    );
  }

  // Error State
  if (isError) {
    return (
      <div className="py-6">
        <div className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-red-800 dark:text-red-300 mb-1">
              Failed to extract requirements
            </p>
            <p className="text-xs text-red-600 dark:text-red-400 mb-3">
              {error instanceof Error ? error.message : 'An unexpected error occurred'}
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={isRefetching}
              className="text-xs"
            >
              <RefreshCw className={`h-3 w-3 mr-1 ${isRefetching ? 'animate-spin' : ''}`} />
              Retry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // No Data State
  if (!requirementsData || requirementsData.total_extracted === 0) {
    return (
      <Card className="p-8 text-center">
        <FileText className="h-12 w-12 text-gray-400 dark:text-gray-600 mx-auto mb-4" />
        <p className="text-gray-600 dark:text-slate-200 mb-2">
          No requirements extracted yet
        </p>
        <p className="text-sm text-gray-500 dark:text-slate-400 mb-4">
          Requirements will be extracted from the opportunity documents when available.
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          disabled={isRefetching}
        >
          <RefreshCw className={`h-3 w-3 mr-1 ${isRefetching ? 'animate-spin' : ''}`} />
          Try Again
        </Button>
      </Card>
    );
  }

  const { requirements_by_type, total_extracted, extraction_source } = requirementsData;

  // Sort requirement types by priority (mandatory first, then alphabetical)
  const typeOrder = ['mandatory', 'technical', 'commercial', 'administrative', 'legal', 'qualification', 'submission', 'evaluation', 'general'];
  const sortedTypes = Object.keys(requirements_by_type).sort((a, b) => {
    const aIndex = typeOrder.indexOf(a);
    const bIndex = typeOrder.indexOf(b);
    if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;
    return a.localeCompare(b);
  });

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-slate-200 mb-1">Total Requirements</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-slate-100">{total_extracted}</p>
            </div>
            <FileText className="h-8 w-8 text-blue-600 dark:text-blue-400" />
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-slate-200 mb-1">Source</p>
              <p className="text-sm font-medium text-gray-900 dark:text-slate-100 capitalize">
                {extraction_source === 'rag_analysis' ? 'AI Extracted' : 'Basic'}
              </p>
            </div>
            <Shield className="h-8 w-8 text-purple-600 dark:text-purple-400" />
          </div>
        </Card>
      </div>

      {/* Requirements by Type */}
      {sortedTypes.map((type) => {
        const typeReqs = requirements_by_type[type];
        const config = REQUIREMENT_TYPE_CONFIG[type] || REQUIREMENT_TYPE_CONFIG.general;
        
        return (
          <Card key={type} className="p-4">
            <div className="flex items-center gap-2 mb-4">
              <div className={`p-2 rounded-lg ${config.bgColor} ${config.color}`}>
                {config.icon}
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100">
                {config.label} Requirements
              </h3>
              <Badge variant="default" className="ml-auto">
                {typeReqs.length} {typeReqs.length === 1 ? 'requirement' : 'requirements'}
              </Badge>
            </div>
            
            <div className="space-y-3">
              {typeReqs.map((req: Requirement, index: number) => (
                <div
                  key={req.requirement_id || index}
                  className={`p-4 rounded-lg border ${
                    req.compliance_mandatory
                      ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700'
                      : 'bg-white dark:bg-slate-700/50 dark:border-slate-600'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex-1">
                      {req.requirement_number && (
                        <span className="text-xs font-mono text-gray-500 dark:text-slate-300 mr-2">
                          {req.requirement_number}
                        </span>
                      )}
                      {req.compliance_mandatory && (
                        <Badge variant="error" className="text-xs">
                          <AlertTriangle className="h-3 w-3 mr-1" />
                          Mandatory
                        </Badge>
                      )}
                      {req.priority && (
                        <Badge variant={req.priority === 'high' ? 'error' : req.priority === 'medium' ? 'warning' : 'default'} className="text-xs ml-2">
                          {req.priority}
                        </Badge>
                      )}
                    </div>
                  </div>
                  
                  <p className="text-sm text-gray-700 dark:text-slate-200 leading-relaxed mb-2">
                    {req.requirement_text}
                  </p>
                  
                  <div className="flex flex-wrap gap-2 text-xs text-gray-500 dark:text-slate-300">
                    {req.section_reference && (
                      <span>Section: {req.section_reference}</span>
                    )}
                    {req.page_number && (
                      <span>Page: {req.page_number}</span>
                    )}
                    {req.evaluation_weight !== undefined && (
                      <span>Weight: {req.evaluation_weight}%</span>
                    )}
                    {req.estimated_effort_hours && (
                      <span>Est. Effort: {req.estimated_effort_hours}h</span>
                    )}
                  </div>
                  
                  {req.staff_skills_required && req.staff_skills_required.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {req.staff_skills_required.map((skill: string, i: number) => (
                        <Badge key={i} variant="default" className="text-xs">
                          {skill}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Card>
        );
      })}
    </div>
  );
}


